import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAdmisionFormulario, enviarAdmision } from '../supabase';
import { C } from '../constants';
import InputFecha from '../components/InputFecha';

// ═══════════════════════════════════════════════════════════════════════════════
// ADMISIÓN — la hoja de datos que llena el paciente
//
// Esta pantalla se abre SIN sesión, desde un link que la clínica manda por
// WhatsApp. Vive fuera de App.js a propósito: no depende de Auth, así que una
// caída del servicio de sesiones —como la del 27 de agosto— no deja al paciente
// mirando una pantalla congelada.
//
// El formulario no está escrito acá. Viene de `admision_plantillas.esquema`, que
// cada clínica arma a su gusto. Este archivo solo sabe pintar tipos de campo.
//
// Se llena por pasos y no en una sola tira: la hoja de Fertility tiene 38
// campos, y 38 campos en un scroll infinito es un formulario abandonado a la
// mitad. Un paso por sección, con barra de avance y validación al pasar.
// ═══════════════════════════════════════════════════════════════════════════════

const K = (seccion, campo) => `${seccion}.${campo}`;

// Las opciones aceptan dos formas: 'Soltera' o { valor, etiqueta }. La primera
// es cómoda de escribir a mano; la segunda hace falta cuando el texto que ve el
// paciente no es el valor que guarda la base (los códigos de doctor, por ejemplo).
const normOpcion = (o) =>
  (o && typeof o === 'object') ? { valor: o.valor, etiqueta: o.etiqueta ?? o.valor }
                               : { valor: o, etiqueta: o };

// Una sección con `toggle` es opcional: la de pareja solo aplica si el paciente
// dice que viene acompañado. Reemplaza al "si no aplica colocar N/A", que
// obligaba a escribir dieciséis veces lo mismo.
const seccionEncendida = (sec, datos) =>
  !sec.toggle || datos[K(sec.id, sec.toggle.id)] === 'si';

export default function AdmisionPublica({ token }) {
  const [cargando, setCargando] = useState(true);
  const [fatal, setFatal]       = useState('');
  const [form, setForm]         = useState(null);
  const [paso, setPaso]         = useState(0);
  const [datos, setDatos]       = useState({});
  const [faltan, setFaltan]     = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado]   = useState(false);

  useEffect(() => {
    let vivo = true;
    getAdmisionFormulario(token)
      .then((f) => { if (vivo) setForm(f); })
      .catch(() => {
        if (vivo) setFatal('Este enlace no está disponible. Puede que haya vencido o que la clínica lo haya reemplazado — solicita uno nuevo.');
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [token]);

  const secciones = useMemo(() => form?.esquema?.secciones || [], [form]);
  const seccion   = secciones[paso] || null;
  const ultimo    = paso === secciones.length - 1;

  const set = useCallback((clave, valor) => {
    setDatos((d) => ({ ...d, [clave]: valor }));
    setFaltan((f) => f.filter((x) => x !== clave));
  }, []);

  // Solo se validan los campos que el paciente realmente está viendo: una
  // sección apagada no puede tener obligatorios.
  const validarPaso = () => {
    if (!seccion || !seccionEncendida(seccion, datos)) return [];
    return (seccion.campos || [])
      .filter((c) => c.req && !String(datos[K(seccion.id, c.id)] ?? '').trim())
      .map((c) => K(seccion.id, c.id));
  };

  const avanzar = () => {
    const f = validarPaso();
    if (f.length) { setFaltan(f); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setPaso((p) => p + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const enviar = async () => {
    const f = validarPaso();
    if (f.length) { setFaltan(f); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setEnviando(true);
    try {
      // Se manda solo lo que tiene contenido, y nada de las secciones apagadas:
      // la base no debería recibir dieciséis llaves vacías de una pareja que no
      // existe, ni tener que adivinar cuáles ignorar.
      const limpio = {};
      for (const sec of secciones) {
        if (!seccionEncendida(sec, datos)) {
          if (sec.toggle) limpio[K(sec.id, sec.toggle.id)] = 'no';
          continue;
        }
        if (sec.toggle) limpio[K(sec.id, sec.toggle.id)] = 'si';
        for (const c of sec.campos || []) {
          const v = String(datos[K(sec.id, c.id)] ?? '').trim();
          if (v) limpio[K(sec.id, c.id)] = v;
        }
      }
      await enviarAdmision(token, limpio);
      setEnviado(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      setFatal(mensajeDeEnvio(e));
    } finally {
      setEnviando(false);
    }
  };

  if (cargando) return <Marco><p style={s.nota}>Cargando el formulario…</p></Marco>;

  if (fatal && !form) return <Marco><Aviso texto={fatal} /></Marco>;

  if (enviado) return (
    <Marco clinica={form?.clinica}>
      <div style={{ textAlign: 'center', padding: '30px 4px' }}>
        <div style={{ fontSize: 42, lineHeight: 1 }}>✓</div>
        <h1 style={{ ...s.h1, marginTop: 14 }}>Listo, ya la recibimos</h1>
        <p style={s.nota}>
          {form?.clinica ? `${form.clinica} ya tiene tus datos.` : 'Ya tenemos tus datos.'} No hace falta que
          hagas nada más — en la clínica los revisan antes de tu consulta.
        </p>
        <p style={{ ...s.nota, marginTop: 14, color: C.mutedL }}>
          Puedes cerrar esta página.
        </p>
      </div>
    </Marco>
  );

  return (
    <Marco clinica={form?.clinica}>
      <h1 style={s.h1}>{form?.esquema?.titulo || form?.nombre || 'Hoja de datos'}</h1>
      {paso === 0 && form?.esquema?.intro && <p style={s.nota}>{form.esquema.intro}</p>}

      <Progreso paso={paso} total={secciones.length} />

      {fatal && <Aviso texto={fatal} onCerrar={() => setFatal('')} />}
      {faltan.length > 0 && (
        <Aviso texto={`Falta${faltan.length === 1 ? '' : 'n'} ${faltan.length} dato${faltan.length === 1 ? '' : 's'} obligatorio${faltan.length === 1 ? '' : 's'} en esta página. Están marcados abajo.`} />
      )}

      {seccion && (
        <>
          <h2 style={s.h2}>{seccion.titulo}</h2>

          {seccion.toggle && (
            <div style={s.toggleCaja}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>{seccion.toggle.etiqueta}</div>
              {seccion.toggle.ayuda && <div style={s.ayuda}>{seccion.toggle.ayuda}</div>}
              <div style={{ display: 'flex', gap: 9, marginTop: 10 }}>
                {[['si', 'Sí'], ['no', 'No']].map(([v, txt]) => {
                  const on = datos[K(seccion.id, seccion.toggle.id)] === v;
                  return (
                    <button key={v} type="button" onClick={() => set(K(seccion.id, seccion.toggle.id), v)}
                      style={{ ...s.opcion, ...(on ? s.opcionOn : null), minWidth: 92 }}>
                      {txt}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {seccionEncendida(seccion, datos) && (seccion.campos || []).map((c) => (
            <Campo
              key={c.id}
              campo={c}
              clave={K(seccion.id, c.id)}
              valor={datos[K(seccion.id, c.id)] ?? ''}
              falta={faltan.includes(K(seccion.id, c.id))}
              onChange={set}
            />
          ))}
        </>
      )}

      <div style={s.pie}>
        {paso > 0 && (
          <button type="button" style={s.btnSec} disabled={enviando}
            onClick={() => { setPaso((p) => p - 1); window.scrollTo({ top: 0 }); }}>
            Atrás
          </button>
        )}
        <button type="button" style={{ ...s.btnPri, opacity: enviando ? 0.6 : 1 }}
          disabled={enviando} onClick={ultimo ? enviar : avanzar}>
          {enviando ? 'Enviando…' : ultimo ? 'Enviar mis datos' : 'Continuar'}
        </button>
      </div>
    </Marco>
  );
}

// ─── Un campo ────────────────────────────────────────────────────────────────
function Campo({ campo, clave, valor, falta, onChange }) {
  const borde = falta ? C.red : C.borderDk;
  const comun = { ...s.input, borderColor: borde };
  const set = (v) => onChange(clave, v);

  return (
    <div style={{ marginBottom: 15 }}>
      <label style={s.label} htmlFor={clave}>
        {campo.etiqueta}
        {campo.req && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>

      {campo.tipo === 'parrafo' ? (
        <textarea id={clave} style={{ ...comun, minHeight: 84, resize: 'vertical' }}
          value={valor} onChange={(e) => set(e.target.value)} />
      ) : campo.tipo === 'lista' ? (
        <select id={clave} style={comun} value={valor} onChange={(e) => set(e.target.value)}>
          <option value="">Elige una opción…</option>
          {(campo.opciones || []).map(normOpcion).map((o) => (
            <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
          ))}
        </select>
      ) : campo.tipo === 'opciones' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(campo.opciones || []).map(normOpcion).map((o) => (
            <button key={o.valor} type="button" onClick={() => set(o.valor)}
              style={{ ...s.opcion, ...(valor === o.valor ? s.opcionOn : null) }}>
              {o.etiqueta}
            </button>
          ))}
        </div>
      ) : campo.tipo === 'fecha' ? (
        <InputFecha id={clave} style={comun} value={valor} onChange={(e) => set(e.target.value)} />
      ) : (
        <input
          id={clave}
          style={comun}
          value={valor}
          onChange={(e) => set(e.target.value)}
          type={campo.tipo === 'numero' ? 'number'
              : campo.tipo === 'correo' ? 'email'
              : campo.tipo === 'telefono' ? 'tel' : 'text'}
          inputMode={campo.tipo === 'numero' ? 'decimal' : campo.tipo === 'telefono' ? 'tel' : undefined}
          autoComplete={campo.tipo === 'correo' ? 'email' : campo.tipo === 'telefono' ? 'tel' : 'off'}
        />
      )}

      {campo.ayuda && <div style={s.ayuda}>{campo.ayuda}</div>}
      {falta && <div style={{ ...s.ayuda, color: C.red, fontWeight: 600 }}>Este dato es necesario.</div>}
    </div>
  );
}

// ─── Marco, progreso y avisos ────────────────────────────────────────────────
function Marco({ clinica, children }) {
  return (
    <div style={s.fondo}>
      <div style={s.hoja}>
        {clinica && <div style={s.clinica}>{clinica}</div>}
        {children}
      </div>
      <div style={s.firma}>InnovaLab</div>
    </div>
  );
}

function Progreso({ paso, total }) {
  if (total <= 1) return null;
  const pct = Math.round(((paso + 1) / total) * 100);
  return (
    <div style={{ margin: '18px 0 22px' }}>
      <div style={{ height: 5, background: C.border, borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: C.teal, transition: 'width .25s' }} />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Paso {paso + 1} de {total}</div>
    </div>
  );
}

function Aviso({ texto, onCerrar }) {
  return (
    <div style={s.aviso}>
      <span style={{ flex: 1 }}>{texto}</span>
      {onCerrar && (
        <button type="button" onClick={onCerrar}
          style={{ background: 'none', border: 'none', color: '#92400e', fontWeight: 800, cursor: 'pointer', fontSize: 16 }}>
          ✕
        </button>
      )}
    </div>
  );
}

// La base rechaza por reglas concretas; el paciente no tiene que leer ninguna.
function mensajeDeEnvio(err) {
  const t = `${err?.message || ''} ${err?.details || ''}`.toLowerCase();
  if (t.includes('demasiados_envios')) return 'Estamos recibiendo muchas respuestas en este momento. Intenta de nuevo en un rato.';
  if (t.includes('formulario_no_disponible')) return 'Este enlace ya no está disponible. Pedile uno nuevo a la clínica.';
  if (t.includes('respuesta_muy_grande')) return 'La respuesta quedó demasiado larga. Acorta los campos de texto e intenta de nuevo.';
  if (t.includes('failed to fetch') || t.includes('networkerror') || t.includes('load failed')) {
    return 'No pudimos enviar tus datos: revisa tu conexión e intenta otra vez. Lo que escribiste sigue aquí.';
  }
  return 'No pudimos enviar tus datos. Intenta otra vez en un momento — lo que escribiste sigue acá.';
}

// ─── Estilos ─────────────────────────────────────────────────────────────────
// Pensados para celular: 16px en los inputs (menos de eso hace que iOS haga zoom
// al tocar el campo) y botones de 46px de alto, que es el mínimo cómodo con el
// pulgar.
const s = {
  fondo: {
    minHeight: '100vh', background: C.bg, padding: '18px 14px 40px',
    fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text,
  },
  hoja: {
    maxWidth: 560, margin: '0 auto', background: C.white, borderRadius: 14,
    border: `1px solid ${C.border}`, padding: '22px 18px 26px',
    boxShadow: '0 2px 14px rgba(13,43,69,.08)',
  },
  clinica: {
    fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
    color: C.teal, marginBottom: 10,
  },
  h1:   { fontSize: 22, fontWeight: 800, color: C.navy, margin: '0 0 8px', lineHeight: 1.25 },
  h2:   { fontSize: 16, fontWeight: 800, color: C.navy, margin: '4px 0 16px' },
  nota: { fontSize: 14, color: C.muted, lineHeight: 1.55, margin: 0 },
  label:{ display: 'block', fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 },
  ayuda:{ fontSize: 12.5, color: C.muted, marginTop: 5, lineHeight: 1.45 },
  input:{
    width: '100%', padding: '12px 13px', border: `1.5px solid ${C.borderDk}`,
    borderRadius: 9, fontSize: 16, fontFamily: 'inherit', color: C.text,
    background: C.white, boxSizing: 'border-box', outline: 'none',
  },
  opcion: {
    padding: '11px 15px', borderRadius: 9, border: `1.5px solid ${C.borderDk}`,
    background: C.white, color: C.text, fontSize: 14.5, fontFamily: 'inherit',
    fontWeight: 600, cursor: 'pointer', minHeight: 46,
  },
  opcionOn: { borderColor: C.teal, background: C.tealL, color: C.navy },
  toggleCaja: {
    border: `1px solid ${C.border}`, background: '#f8fafc', borderRadius: 11,
    padding: '14px 15px', marginBottom: 20,
  },
  aviso: {
    display: 'flex', gap: 10, alignItems: 'flex-start',
    background: C.amberL, border: `1px solid ${C.amber}`, color: '#92400e',
    borderRadius: 10, padding: '11px 13px', fontSize: 13.5, lineHeight: 1.5,
    margin: '14px 0',
  },
  pie: { display: 'flex', gap: 10, marginTop: 26 },
  btnPri: {
    flex: 1, minHeight: 48, borderRadius: 10, border: 'none', background: C.teal,
    color: C.white, fontSize: 15.5, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
  },
  btnSec: {
    minHeight: 48, padding: '0 20px', borderRadius: 10, border: `1.5px solid ${C.borderDk}`,
    background: C.white, color: C.muted, fontSize: 15, fontWeight: 700,
    fontFamily: 'inherit', cursor: 'pointer',
  },
  firma: {
    textAlign: 'center', fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
    color: C.mutedL, marginTop: 20, textTransform: 'uppercase',
  },
};
